import React, { useContext, useState } from 'react';
import { CRMContext } from '../context/CRMContext';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiPlus, FiSearch, FiChevronRight, FiTag } from 'react-icons/fi';
import { LeadModal } from '../components/Modals';
import { format } from 'date-fns';

const statusStyles = {
  New:       { backgroundColor: '#EFF6FF', color: '#2563EB', label: 'Nuevo' },
  Contacted: { backgroundColor: '#F0F9FF', color: '#0284C7', label: 'Contactado' },
  Qualified: { backgroundColor: '#F0FDF4', color: '#16A34A', label: 'Calificado' },
  Won:       { backgroundColor: '#FFFBEB', color: '#D97706', label: 'Ganado' },
  Lost:      { backgroundColor: '#F1F5F9', color: '#64748B', label: 'Perdido' },
};

const LeadsList = () => {
  const { leads, addLead, getLeadNextFollowUp } = useContext(CRMContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) || (lead.company && lead.company.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'All' || lead.status === statusFilter;
    const matchesSource = sourceFilter === 'All' || lead.source === sourceFilter;
    return matchesSearch && matchesStatus && matchesSource;
  });

  const sources = ['All', 'Website', 'GMB', 'Facebook', 'Instagram', 'Recommended', 'Other'];
  const sourceLabels = {
    All: 'Cualquier origen',
    Website: 'Sitio Web',
    GMB: 'GMB',
    Facebook: 'Facebook',
    Instagram: 'Instagram',
    Recommended: 'Recomendado',
    Other: 'Otro'
  };

  const selectStyle = {
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    padding: '8px 12px',
    outline: 'none',
    backgroundColor: 'white',
    fontSize: '14px',
    color: '#0F172A',
  };

  return (
    <div className="max-w-6xl mx-auto h-full flex flex-col">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Directorio de Leads</h1>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white w-full sm:w-auto justify-center transition-all text-sm"
          style={{ backgroundColor: '#2563EB', boxShadow: '0 1px 3px rgba(37,99,235,0.3)' }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1D4ED8'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2563EB'}
        >
          <SafeIcon icon={FiPlus} /> Agregar Lead
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl mb-6 flex flex-col xl:flex-row gap-4" style={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-base" style={{ color: '#94A3B8' }} />
          <input 
            type="text" placeholder="Buscar leads..." 
            className="w-full pl-10 pr-4 py-2 rounded-lg text-sm outline-none"
            style={{ border: '1px solid #E2E8F0', color: '#0F172A' }}
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            onFocus={e => e.target.style.borderColor = '#2563EB'}
            onBlur={e => e.target.style.borderColor = '#E2E8F0'}
          />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Estado:</span>
            <select style={selectStyle} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="All">Cualquier estado</option>
              {['New', 'Contacted', 'Qualified', 'Lost', 'Won'].map(s => (
                <option key={s} value={s}>{statusStyles[s].label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Origen:</span>
            <select style={selectStyle} value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
              {sources.map(s => <option key={s} value={s}>{sourceLabels[s]}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 bg-white rounded-xl overflow-hidden flex flex-col" style={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-left border-collapse">
            <thead style={{ backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
              <tr>
                {['Nombre y Empresa', 'Contacto Principal', 'Estado y Origen', 'Próxima Tarea', 'Acción'].map((h, i) => (
                  <th key={h} className={`p-4 text-[10px] font-bold uppercase tracking-wider ${i >= 1 && i <= 2 ? (i === 1 ? 'hidden md:table-cell' : '') : i === 3 ? 'hidden lg:table-cell' : i === 4 ? 'text-right' : ''}`} style={{ color: '#94A3B8' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredLeads.length === 0 ? (
                <tr><td colSpan="5" className="p-12 text-center text-sm italic" style={{ color: '#94A3B8' }}>No se encontraron leads.</td></tr>
              ) : filteredLeads.map(lead => {
                const nextFollowUp = getLeadNextFollowUp(lead.id);
                const config = statusStyles[lead.status] || statusStyles.Lost;
                return (
                  <tr key={lead.id} className="group transition-colors" style={{ borderBottom: '1px solid #F1F5F9' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                    <td className="p-4">
                      <p className="font-semibold text-sm" style={{ color: '#0F172A' }}>{lead.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: '#64748B' }}>{lead.company || 'Persona Particular'}</p>
                    </td>
                    <td className="p-4 hidden md:table-cell">
                      <p className="text-sm" style={{ color: '#334155' }}>{lead.emails?.[0]?.value || '—'}</p>
                      <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>{lead.phones?.[0]?.value || '—'}</p>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1.5">
                        <span className="w-fit px-2 py-0.5 text-[10px] font-bold uppercase rounded-md" style={{ backgroundColor: config.backgroundColor, color: config.color }}>
                          {config.label}
                        </span>
                        <span className="text-[10px] font-medium flex items-center gap-1" style={{ color: '#94A3B8' }}>
                          <SafeIcon icon={FiTag} className="text-[8px]" /> {sourceLabels[lead.source] || lead.source}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 hidden lg:table-cell text-sm font-medium" style={{ color: '#334155' }}>
                      {nextFollowUp ? format(new Date(nextFollowUp), 'd MMM, yyyy') : <span style={{ color: '#CBD5E1' }}>Ninguna</span>}
                    </td>
                    <td className="p-4 text-right">
                      <Link to={`/leads/${lead.id}`} className="inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors" style={{ color: '#94A3B8' }} onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#EFF6FF'; e.currentTarget.style.color = '#2563EB'; }} onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#94A3B8'; }}>
                        <SafeIcon icon={FiChevronRight} className="text-xl" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <LeadModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={addLead} />
    </div>
  );
};

export default LeadsList;